import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, X } from "lucide-react";

export default function ELearningTab() {
  const [videos, setVideos] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [modal, setModal] = useState(null); // {type: 'video'|'ebook'}

  const load = async () => {
    try {
      const [v, e] = await Promise.all([api.get("/videos"), api.get("/ebooks")]);
      setVideos(v.data);
      setEbooks(e.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (kind, id) => {
    if (!window.confirm("Hapus?")) return;
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Terhapus");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="elearning-tab">
      <Section
        title="Video Edukasi"
        onAdd={() => setModal({ type: "video" })}
        empty="Belum ada video."
        items={videos.map((v) => ({
          id: v.id, title: v.title, subtitle: v.description, image: v.thumbnail || null,
          onDelete: () => del("videos", v.id),
        }))}
      />
      <Section
        title="E-Book"
        onAdd={() => setModal({ type: "ebook" })}
        empty="Belum ada e-book."
        items={ebooks.map((e) => ({
          id: e.id, title: e.title, subtitle: e.description, image: e.thumbnail || null,
          onDelete: () => del("ebooks", e.id),
        }))}
      />

      {modal && (
        <AddModal
          kind={modal.type}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function Section({ title, onAdd, items, empty }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl md:text-2xl">{title}</h3>
        <button
          onClick={onAdd}
          data-testid={`add-${title.toLowerCase().split(" ")[0]}`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] text-sm"
        >
          <Plus size={14} /> Tambah
        </button>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-stone-300 p-8 text-center text-stone-500 text-sm">{empty}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="border border-stone-200 bg-white p-4 flex gap-3">
              {it.image && <img src={it.image} alt="" className="w-16 h-20 object-cover flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{it.title}</div>
                <div className="text-xs text-stone-500 line-clamp-2">{it.subtitle}</div>
              </div>
              <button onClick={it.onDelete} className="p-2 text-stone-500 hover:text-red-600 flex-shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddModal({ kind, onClose, onSaved }) {
  const [f, setF] = useState({ title: "", description: "", url: "", thumbnail: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { title: f.title, description: f.description, thumbnail: f.thumbnail };
      if (kind === "video") payload.embed_url = f.url;
      else payload.file_url = f.url;
      await api.post(`/admin/${kind === "video" ? "videos" : "ebooks"}`, payload);
      toast.success("Ditambahkan");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h3 className="font-display text-2xl">Tambah {kind === "video" ? "Video" : "E-Book"}</h3>
          <button onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Judul" value={f.title} onChange={(v) => setF({ ...f, title: v })} required testId="el-title" />
          <Field label="Deskripsi" value={f.description} onChange={(v) => setF({ ...f, description: v })} textarea testId="el-desc" />
          <Field
            label={kind === "video" ? "Embed URL (YouTube embed)" : "File URL (PDF)"}
            value={f.url}
            onChange={(v) => setF({ ...f, url: v })}
            required
            testId="el-url"
            placeholder={kind === "video" ? "https://www.youtube.com/embed/..." : "https://.../file.pdf"}
          />
          <Field label="Thumbnail URL (opsional)" value={f.thumbnail} onChange={(v) => setF({ ...f, thumbnail: v })} testId="el-thumb" />
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-stone-300">Batal</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="el-submit"
              className="px-4 py-2 bg-[#0A0A0A] text-white hover:bg-[#F39237] hover:text-[#0A0A0A] disabled:opacity-60"
            >
              {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, textarea, testId, placeholder }) {
  return (
    <div>
      <label className="block overline text-stone-500 mb-2">{label}</label>
      {textarea ? (
        <textarea
          data-testid={testId}
          required={required}
          value={value}
          rows={2}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-stone-300 focus:border-[#F39237] outline-none"
        />
      ) : (
        <input
          type="text"
          data-testid={testId}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-11 px-3 border border-stone-300 focus:border-[#F39237] outline-none"
        />
      )}
    </div>
  );
}
